import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Play, ArrowDown } from "lucide-react";

const scrollToBooking = () => {
  const el = document.getElementById("booking");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const triggerAudio = () => window.dispatchEvent(new CustomEvent("cadence:play"));

const SLIDES = [
  { src: "/assets/hero-folded-hands.jpg", pos: "50% 22%", alt: "Portrait of Casey Muze" },
  { src: "/assets/hero-wall.jpg", pos: "45% 28%", alt: "Casey Muze smiling outdoors" },
  { src: "/assets/hero-casey-classroom.jpg", pos: "60% 0%", alt: "Casey Muze speaking to a group" },
];

const HeroSlideshow = () => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setActive((i) => (i + 1) % SLIDES.length), 5000);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="absolute inset-0" data-testid="hero-slideshow" aria-hidden="true">
      {SLIDES.map((s, i) => (
        <img
          key={s.src}
          src={s.src}
          alt={s.alt}
          className="absolute inset-0 h-full w-full object-cover transition-opacity duration-[2200ms] ease-in-out"
          style={{ objectPosition: s.pos, opacity: i === active ? 1 : 0 }}
        />
      ))}
    </div>
  );
};

export const CadenceHero = () => (
  <section
    className="relative flex min-h-[100svh] items-center overflow-hidden bg-[#0A2E2C]"
    data-testid="cadence-hero"
  >
    {/* full-bleed crossfading hero photos */}
    <HeroSlideshow />
    {/* legibility scrims */}
    <div className="absolute inset-0" style={{ background: "linear-gradient(100deg, rgba(10,46,44,0.94) 0%, rgba(10,46,44,0.8) 42%, rgba(10,46,44,0.5) 100%)" }} />
    <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(10,46,44,0.35) 0%, transparent 35%, rgba(10,46,44,0.85) 100%)" }} />

    <div className="relative z-10 mx-auto w-full max-w-6xl px-6 py-28 sm:px-10 lg:px-12">
      <motion.img
        src="/assets/casey-logo-plum.png"
        alt="Casey Muze"
        data-testid="hero-logo"
        className="mb-8 h-20 w-auto drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)] sm:h-24 lg:h-28"
        style={{ filter: "brightness(1.55) saturate(1.15)" }}
        initial={{ opacity: 0, y: 10, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} transition={{ duration: 1 }}
      />
      <motion.p
        className="font-mono text-[0.7rem] uppercase tracking-[0.32em] text-[#D4AF37]"
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.8 }}
      >
        Casey&#39;s Cadence · Keynotes · Workshops · Corporate Wellbeing
      </motion.p>

      <motion.h1
        className="mt-6 max-w-3xl font-serif text-4xl font-light leading-[1.02] tracking-tight text-[#F8FAFC] sm:text-5xl md:text-6xl lg:text-7xl"
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 1 }}
      >
        What Is Your <span className="italic text-[#D4AF37]">Cadence?</span>
      </motion.h1>

      <motion.p
        className="mt-7 max-w-2xl text-base leading-relaxed text-[#F8FAFC]/85 sm:text-lg"
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 1 }}
      >
        Burnout, disengagement, and fractured culture share one root — a nervous system out of rhythm.
        Casey Muze helps mental health providers, community organizations, and corporate teams restore
        focus, connection, and resilience through science-backed bilateral-rhythm keynotes and workshops.
      </motion.p>

      <motion.div
        className="mt-11 flex w-full flex-col items-stretch gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4"
        initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45, duration: 0.9 }}
      >
        <button
          onClick={scrollToBooking}
          data-testid="hero-book-cta"
          data-cursor="org"
          className="group inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#D4AF37] px-8 py-4 text-sm font-bold text-[#0A2E2C] shadow-lg shadow-[#0A2E2C]/40 transition-all hover:scale-[1.03] hover:bg-[#B99628] sm:w-auto"
        >
          Book Casey&#39;s Keynotes &amp; Consults
          <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-0.5" />
        </button>

        <button
          onClick={triggerAudio}
          data-testid="hero-audio-cta"
          data-cursor="rhythm"
          className="inline-flex w-full items-center justify-center gap-3 rounded-full border border-[#AEE6D5]/30 bg-white/10 px-6 py-4 text-sm text-[#F8FAFC] backdrop-blur-xl transition-colors hover:bg-white/20 sm:w-auto"
        >
          <Play className="h-4 w-4 text-[#AEE6D5]" />
          Listen to the Healing Cadence
        </button>
      </motion.div>
    </div>
  </section>
);
